import { useMemo } from 'react';

import { useIntl } from 'react-intl-next';

import { fg } from '@atlaskit/platform-feature-flags';

import { ApplicationModes } from '../../../common/constants/application-modes';
import { ProductKeys, products } from '../../../common/constants/products';
import { getProductOrder } from '../../../common/utils/product';

import { inputPlaceholderMessages, skeletonPlaceholders } from './messages';
import {
	getGhostTextInputPlaceholder,
	ghostTextInputPlaceholders,
	useGhostTextSessionCount,
} from './utils';

export type UsePlaceholderTextProps = {
	applicationMode: ApplicationModes;
	isAdminHubAIEnabled: boolean;
	isNav4Enabled: boolean;
	quickFindOpen: boolean;
	availableProducts: ProductKeys[];
};

export const usePlaceholderText = ({
	applicationMode,
	isAdminHubAIEnabled,
	isNav4Enabled,
	quickFindOpen,
	availableProducts,
}: UsePlaceholderTextProps) => {
	const { formatMessage } = useIntl();
	const [ghostTextSession, { incrementSession }] = useGhostTextSessionCount({
		initialValue: -1, // Off by 1 because initially each page load increments the session count
		maxCount: ghostTextInputPlaceholders.length,
	});

	const productDisplayNames = useMemo(() => {
		const productConfigs = availableProducts
			.sort(getProductOrder)
			.map((productKey) => products[productKey]);

		const firstPartyProducts = productConfigs
			.filter((product) => !product.is3P)
			.map((product) => product.displayName);

		const thirdPartyProducts = productConfigs
			.filter((product) => product.is3P)
			.map((product) => product.displayName);

		return [...thirdPartyProducts.slice(0, 2), firstPartyProducts[0]].join(', ');
	}, [availableProducts]);

	const placeholder = useMemo(() => {
		const placeholders = [
			[
				!isNav4Enabled && !quickFindOpen,
				skeletonPlaceholders.inputPlaceholderNav3, // Search
			],
			[
				applicationMode === ApplicationModes.Unified,
				skeletonPlaceholders.inputPlaceholderDefault, // Search for anything
			],
			[
				applicationMode === ApplicationModes.Rovo && fg('quick_find_query_filters_and_ghost_text'),
				getGhostTextInputPlaceholder(
					Math.max(ghostTextSession, 0), // Ensure session count is positive
					0, // TODO: >>>> Hardcoded for now but update on an interval with an animation
				), // Search across {products}, and more  - Dynamic ghost text
			],
			[
				applicationMode === ApplicationModes.Rovo,
				// TODO: This was removed due to a bug where it would change the placeholder 3 times during page load
				// https://product-fabric.atlassian.net/browse/QS-6019
				// inputPlaceholderMessages.inputPlaceholderWithRovo, // Search or ask Rovo
				skeletonPlaceholders.inputPlaceholderDefault, // Search for anything
			],
			[
				isAdminHubAIEnabled,
				inputPlaceholderMessages.inputPlaceholderWithAtlassianIntelligence, // Search or ask anything
			],
		] as const;

		const [, text] = placeholders.find(([predicate]) => predicate) ?? [
			true,
			skeletonPlaceholders.inputPlaceholderDefault, // Search for anything
		];

		return formatMessage(text, { products: productDisplayNames });
	}, [
		applicationMode,
		formatMessage,
		ghostTextSession,
		isAdminHubAIEnabled,
		isNav4Enabled,
		productDisplayNames,
		quickFindOpen,
	]);

	return { placeholder, incrementSession, ghostTextSession };
};
